/* jshint nomen:false */
/**
 * Tile layer for Leaflet that uses the
 * <a href="https://developer.tomtom.com/online-traffic/online-traffic-documentation-online-vector-traffic-incidents/
 * vector-flow-tiles">Vector Traffic Incidents</a> from TomTom Online Traffic.
 *
 * The current vector renderer implementation is based on the
 * [Mapbox GL JS library](https://www.mapbox.com/mapbox-gl-js/api/).
 *
 * @example
 * ```js
 * var ttVectorTrafficIncidents = new L.TomTomVectorTrafficIncidents({
 *      key: '<Your Traffic Flow API key>',
 *      refresh: 30000,
 *      basePath: '<SDK base path>',
 *      style: 's1'
 * });
 *
 * map.addLayer(ttVectorTrafficIncidents);
 * ```
 *
 * @class TomTomVectorTrafficIncidents
 * @namespace L
 * @module Map
 * @extends L.TomTomVectorLayer
 * @constructor
 * @param {"s1"|"s2"|"s3"|"night"} [options.style="s1"] Specifies the style and type of traffic incidents
 * @param {String} [options.key] Traffic Flow API key that can be obtained from
 *     <a href="http://developer.tomtom.com">TomTom's developer portal</a>.
 * @param {Integer} [options.refresh=30000] Traffic refresh interval in milliseconds. The default value is 30 seconds.
 * If a lower value is provided the default value is used.
**/
define([
    'leaflet',
    './vectorLayer',
    '../labelsLayerMixinFactory',
    'security',
    'apiKeyNames',
    '../trafficRefresher'],
function(
    L,
    VectorLayer,
    labelsLayerMixinFactory,
    security,
    ApiKeyNames,
    trafficRefresher
) {
    var TomTomVectorIncidentsLayer;

    function replaceTrafficModelIdInUrl(style, trafficModelId) {
        if (!style) {
            return undefined;
        }
        style.sources.vectorTilesTraffic.tiles.forEach(function(elem, index, arr) {
            var regex = /&t=[0-9]\d+$/;
            var matches = elem.search(regex);
            if (matches > -1) {
                arr[index] = elem.replace(regex, '&t=' + trafficModelId);
            } else {
                arr[index] = elem + '&t=' + trafficModelId;
            }
        }, this);
        return style;
    }

    TomTomVectorIncidentsLayer = VectorLayer.extend({
        defaultOptions: {
            refresh: 30000,
            style: 's1',
            basePath: '/sdk',
            preSetStyleCallback: function(style) {
                return Promise.resolve(replaceTrafficModelIdInUrl(style, this.options.trafficModelId));
            }
        },
        name: 'vectorTrafficIncidents',
        layerSourceName: 'vectorTilesTraffic',
        initialize: function(options) {
            options = options || {};
            if (!options.key && !security.getKey(ApiKeyNames.traffic)) {
                throw new Error('Error during Vector Incident Layer initialization - missing API key');
            }
            if (options.key) {
                security.setKey(ApiKeyNames.traffic, options.key);
            }
            this.options = L.extend(this.options, options);
            this.defaultOptions.preSetStyleCallback = this.defaultOptions.preSetStyleCallback.bind(this);
            this.layerOptions = this.getLayerOptions(options);
            VectorLayer.prototype.initialize.call(this, this.layerOptions);

            this.refresh = this.refresh.bind(this);
        },
        onAdd: function(map) {
            var that = this;
            this.startRefresh().then(function() {
                VectorLayer.prototype.onAdd.call(that, map);
                that.bringToFront();
            });
        },
        onRemove: function(map) {
            this.stopRefresh();
            VectorLayer.prototype.onRemove.call(this, map);
        },
        getLayerOptions: function(opts) {
            var layer = (opts && opts.style) || this.defaultOptions.style;
            var options = L.extend({}, this.defaultOptions, opts);

            return L.extend({}, options, {
                style: 'vectorTrafficIncidents',
                layer: layer,
                refresh: Math.max(this.defaultOptions.refresh, options.refresh),
                securityKeyName: ApiKeyNames.traffic,
                vector: {
                    renderer: {
                        refreshExpiredTiles: false
                    }
                },
                styleUrlMapping: {
                    vectorTrafficIncidents: {
                        's1': options.basePath + '/styles/incidents_s1.json',
                        's2': options.basePath + '/styles/incidents_s2.json',
                        's3': options.basePath + '/styles/incidents_s3.json',
                        'night': options.basePath + '/styles/incidents_night.json'
                    }
                }
            });
        },
        startRefresh: function() {
            var that = this;
            if (this.options._noRefresher) {
                return Promise.resolve();
            }
            return new Promise(function(resolve) {
                that.trafficRefresherIntervalId =
                trafficRefresher.addRefresherWithModelId(undefined, function(trafficModelId) {
                    that.setTrafficModelId(trafficModelId);
                    resolve();
                }, that);
            });
        },
        stopRefresh: function() {
            trafficRefresher.remove(this.trafficRefresherIntervalId);
        },
        refresh: function() {
            var style;
            if (this._glMap) {
                style = this._glMap.getStyle();
                if (style) {
                    style = replaceTrafficModelIdInUrl(style, this.options.trafficModelId);
                    this._glMap.setStyle(style, {
                        diff: false,
                        securityKeyName: ApiKeyNames.traffic
                    });
                }
            }
        },
        setTrafficModelId: function(trafficModelId) {
            if (this.options.trafficModelId !== trafficModelId) {
                this.options.trafficModelId = trafficModelId;
                this.refresh();
            }
        },
        updateOptions: function(options) {
            if (options.style) {
                this.stopRefresh();
                this.layerOptions = this.getLayerOptions(options);
                VectorLayer.prototype.updateOptions.call(this, this.layerOptions);
                this.startRefresh();
            }
        }
    });

    L.TomTomVectorIncidentsLayer = TomTomVectorIncidentsLayer.extend(labelsLayerMixinFactory('vectorTrafficIncidents'));

    L.tomTomVectorIncidentsLayer = function tomTomVectorIncidentsLayer(options) {
        return new L.TomTomVectorIncidentsLayer(options);
    };

    return L.tomTomVectorIncidentsLayer;

});



// WEBPACK FOOTER //
// ./display/layers/vector/vectorTrafficIncidents.js